import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaIndustry,
  FaMicroscope,
  FaChartLine,
  FaRobot,
  FaCheckCircle,
  FaArrowRight,
  FaShieldAlt,
  FaClock,
  FaDollarSign,
  FaUsers,
} from "react-icons/fa";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.12 },
  }),
};

export default function Home() {
  return (
    <main className="bg-white text-gray-800 font-sans">

      {/* ── Hero ── */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-sky-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top_right,_#38bdf8_0%,_transparent_70%)]" />
        <div className="max-w-7xl mx-auto px-6 xl:px-24 py-28 md:py-36 relative z-10">
          <motion.p
            className="text-sky-300 text-sm font-semibold uppercase tracking-widest mb-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
          >
            Trusted by Global Life Sciences Leaders
          </motion.p>
          <motion.h1
            className="text-4xl md:text-6xl font-extrabold leading-tight max-w-4xl mb-6"
            initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          >
            Accelerate Your Digital Transformation in Life Sciences &amp; Industry
          </motion.h1>
          <motion.p
            className="text-lg md:text-xl text-blue-100 max-w-2xl mb-10"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.7 }}
          >
            XKMINNE Technologies delivers AI-powered supply chain intelligence, SAP transformation, and smart manufacturing — helping regulated enterprises cut costs, achieve compliance, and go live in weeks, not months.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row gap-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.6 }}
          >
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-blue-800 font-bold px-8 py-4 rounded-lg shadow-lg hover:bg-sky-50 transition-colors text-base"
            >
              Schedule a Free Consultation <FaArrowRight />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center justify-center gap-2 border border-white/40 text-white font-semibold px-8 py-4 rounded-lg hover:bg-white/10 transition-colors text-base"
            >
              Explore Our Solutions
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── Trust Bar ── */}
      <section className="bg-gray-50 border-y border-gray-100 py-10 px-6 xl:px-24">
        <p className="text-center text-xs font-semibold uppercase tracking-widest text-gray-400 mb-8">
          Trusted Partners &amp; Clients
        </p>
        <div className="flex flex-wrap justify-center items-center gap-12 max-w-4xl mx-auto">
          {[
            { logo: "/logos/abbott-new.png", name: "Abbott" },
            { logo: "/logos/abbvie-new.png", name: "AbbVie" },
            { logo: "/logos/zoll-new.png", name: "ZOLL Respicardia" },
          ].map((partner, i) => (
            <motion.img
              key={i}
              src={partner.logo}
              alt={partner.name}
              className="h-10 object-contain grayscale hover:grayscale-0 transition-all opacity-60 hover:opacity-100"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 0.6 }}
              viewport={{ once: true }}
            />
          ))}
        </div>
      </section>

      {/* ── Solutions ── */}
      <section id="solutions" className="py-24 px-6 xl:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-sky-600 text-sm font-semibold uppercase tracking-widest mb-2">What We Do</p>
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">End-to-End Solutions for Regulated Industries</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">From intelligent manufacturing to SAP-integrated supply chains, we engineer the digital infrastructure that keeps your operations compliant and competitive.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <FaIndustry className="text-3xl text-sky-600" />,
                title: "Manufacturing AI",
                desc: "Automate quality inspection, optimize throughput, and enable predictive maintenance — reducing downtime and defects across your shop floor.",
                link: "/services",
              },
              {
                icon: <FaMicroscope className="text-3xl text-emerald-600" />,
                title: "Life Sciences MES",
                desc: "FDA-compliant manufacturing execution systems that transform paper-based workflows into auditable, real-time digital operations.",
                link: "/services",
              },
              {
                icon: <FaChartLine className="text-3xl text-violet-600" />,
                title: "Supply Chain Intelligence",
                desc: "SAP IBP, PP/DS, and TM integrated with LLM-powered analytics — delivering end-to-end visibility and adaptive decision-making.",
                link: "/services",
              },
              {
                icon: <FaRobot className="text-3xl text-rose-500" />,
                title: "Digital Twin Execution",
                desc: "Bridge OT and IT with real-time digital twin models that provide actionable feedback loops across your entire production network.",
                link: "/services",
              },
            ].map((s, i) => (
              <motion.div
                key={i}
                className="group bg-white rounded-xl p-7 border border-gray-100 shadow-sm hover:shadow-md hover:border-blue-200 transition-all cursor-pointer"
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <div className="mb-5 p-3 inline-flex rounded-lg bg-gray-50 group-hover:bg-blue-50 transition-colors">
                  {s.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-4">{s.desc}</p>
                <Link to={s.link} className="text-sm font-semibold text-blue-700 hover:text-blue-900 inline-flex items-center gap-1">
                  Learn more <FaArrowRight className="text-xs" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why XKMINNE ── */}
      <section className="py-24 px-6 xl:px-24 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-sky-400 text-sm font-semibold uppercase tracking-widest mb-2">Why Choose Us</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Built for Results, Not Just Implementations</h2>
            <p className="text-blue-200 max-w-2xl mx-auto">We combine deep domain expertise with cutting-edge AI to deliver measurable outcomes — not just software deployments.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <FaShieldAlt className="text-2xl text-sky-400" />,
                title: "Compliance-First Design",
                desc: "Every solution is architected for FDA, ISO, and GxP compliance from day one — not bolted on after.",
              },
              {
                icon: <FaClock className="text-2xl text-emerald-400" />,
                title: "6-Week Go-Live",
                desc: "Proven rapid deployment methodology gets your team productive fast, without sacrificing quality.",
              },
              {
                icon: <FaDollarSign className="text-2xl text-amber-400" />,
                title: "Guaranteed ROI",
                desc: "Our clients average 40% lead time reduction and $2.4M+ in measurable cost savings.",
              },
              {
                icon: <FaUsers className="text-2xl text-violet-400" />,
                title: "Domain Experts",
                desc: "10+ years of hands-on Class III device and pharma operations experience on every engagement.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="p-6 rounded-xl bg-white/10 border border-white/10 hover:bg-white/15 transition-colors"
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <div className="mb-4">{item.icon}</div>
                <h3 className="font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-blue-200 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section id="metrics" className="py-24 px-6 xl:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-sky-600 text-sm font-semibold uppercase tracking-widest mb-2">Proven Impact</p>
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">Results Our Clients Measure</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "99.9%", label: "Compliance Accuracy", sub: "Across FDA-regulated deployments" },
              { value: "40%", label: "Lead Time Reduction", sub: "Average across client engagements" },
              { value: "$2.4M+", label: "Cost Savings Delivered", sub: "Per client implementation" },
              { value: "6 Weeks", label: "Average Go-Live", sub: "From kickoff to production" },
            ].map((m, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="p-8 rounded-2xl bg-gradient-to-br from-sky-50 to-blue-50 border border-blue-100"
              >
                <div className="text-4xl md:text-5xl font-extrabold text-blue-800 mb-2">{m.value}</div>
                <p className="font-semibold text-gray-800 mb-1">{m.label}</p>
                <p className="text-xs text-gray-500">{m.sub}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How We Work ── */}
      <section className="py-24 px-6 xl:px-24 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-sky-600 text-sm font-semibold uppercase tracking-widest mb-2">Our Process</p>
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">How We Deliver Your Transformation</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">A structured, transparent engagement model that minimizes risk and maximizes value at every stage.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6 relative">
            {[
              { step: "01", title: "Discovery", desc: "We audit your current operations, systems, and pain points to define the highest-impact opportunities." },
              { step: "02", title: "Solution Design", desc: "Our architects design a compliance-ready roadmap tailored to your regulatory environment and tech stack." },
              { step: "03", title: "Rapid Deployment", desc: "Agile sprints get working solutions into your hands fast, with regular stakeholder reviews built in." },
              { step: "04", title: "Optimize & Scale", desc: "We stay engaged post-launch to measure outcomes, tune performance, and expand capabilities." },
            ].map((s, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm relative"
              >
                <span className="text-4xl font-extrabold text-blue-100 absolute top-4 right-5">{s.step}</span>
                <h3 className="text-base font-bold text-blue-900 mb-2 relative">{s.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonial / Social Proof ── */}
      <section className="py-24 px-6 xl:px-24 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-sky-600 text-sm font-semibold uppercase tracking-widest mb-2">Client Results</p>
            <h2 className="text-3xl font-bold text-blue-900 mb-4">What Our Clients Achieve</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                result: "99.7% audit pass rate",
                detail: "A major medical device manufacturer moved from manual paper-based batch records to a fully digital MES, achieving near-perfect compliance scores on their first FDA audit.",
                industry: "Class III Medical Devices",
              },
              {
                result: "38% supply chain cost reduction",
                detail: "A global pharma company integrated SAP IBP with XKMINNE's LLM-driven demand sensing, eliminating excess inventory and reducing expedite freight costs dramatically.",
                industry: "Pharmaceutical Manufacturing",
              },
              {
                result: "6-week ERP go-live",
                detail: "An industrial manufacturer needed a rapid SAP S/4HANA rollout. XKMINNE's proven deployment playbook delivered a fully live system in 42 days — on time and under budget.",
                industry: "Industrial Manufacturing",
              },
            ].map((t, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="p-7 rounded-xl bg-gray-50 border border-gray-100 flex flex-col"
              >
                <div className="flex items-start gap-2 mb-4">
                  <FaCheckCircle className="text-emerald-500 mt-0.5 flex-shrink-0 text-lg" />
                  <span className="text-lg font-bold text-blue-900">{t.result}</span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed flex-1">{t.detail}</p>
                <span className="mt-5 text-xs font-semibold text-sky-600 uppercase tracking-wide">{t.industry}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section className="py-24 bg-gradient-to-br from-blue-800 to-sky-700 text-white text-center px-6">
        <div className="max-w-3xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-extrabold mb-4"
          >
            Ready to Transform Your Operations?
          </motion.h2>
          <p className="text-blue-200 text-lg mb-10 max-w-xl mx-auto">
            Book a no-obligation 30-minute strategy call with our team. We'll assess your current challenges and outline a concrete path to ROI.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-blue-800 font-bold px-8 py-4 rounded-lg shadow-lg hover:bg-sky-50 transition-colors"
            >
              Schedule Your Free Consultation <FaArrowRight />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center justify-center border border-white/40 text-white font-semibold px-8 py-4 rounded-lg hover:bg-white/10 transition-colors"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-gray-900 text-gray-400 py-16 px-6 xl:px-24">
        <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <span className="text-2xl font-extrabold text-white">
              XK<span className="text-sky-400">MINNE</span>
            </span>
            <p className="mt-3 text-sm text-gray-400 max-w-xs leading-relaxed">
              AI-powered supply chain and manufacturing solutions for regulated global enterprises.
            </p>
            <p className="mt-4 text-sm text-gray-500">
              &copy; {new Date().getFullYear()} XKMINNE Technologies. All rights reserved.
            </p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wide">Company</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/founder" className="hover:text-white transition-colors">Leadership</Link></li>
              <li><Link to="/investor" className="hover:text-white transition-colors">Investors</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wide">Contact</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/contact" className="hover:text-white transition-colors">Get a Consultation</Link></li>
              <li>
                <a href="mailto:founder@xkminne.com" className="hover:text-white transition-colors">
                  founder@xkminne.com
                </a>
              </li>
              <li><Link to="/services" className="hover:text-white transition-colors">Our Services</Link></li>
            </ul>
          </div>
        </div>
      </footer>
    </main>
  );
}
